import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import { i18n } from "@/i18n";
import { cn } from "@/lib/utils";

const HOUR_MS = 60 * 60 * 1000;
const MINUTE_MS = 60 * 1000;

export interface ExpiryPresentation {
  label: string;
  tone: "neutral" | "warning" | "destructive";
}

export function getExpiryPresentation(
  expiresAt: string | null,
  now: Date,
  t: TFunction = i18n.getFixedT(null, "approval"),
): ExpiryPresentation {
  if (expiresAt === null) return { label: t("expiry.none"), tone: "neutral" };
  const remaining = new Date(expiresAt).getTime() - now.getTime();
  if (remaining <= 0) return { label: t("expiry.expired"), tone: "destructive" };

  const totalMinutes = Math.max(1, Math.ceil(remaining / MINUTE_MS));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const label =
    hours > 0 ? t("expiry.hoursMinutes", { hours, minutes }) : t("expiry.minutesOnly", { minutes });
  if (remaining <= 6 * HOUR_MS) return { label, tone: "destructive" };
  if (remaining <= 24 * HOUR_MS) return { label, tone: "warning" };
  return { label, tone: "neutral" };
}

interface ProposalExpiryProps {
  expiresAt: string | null;
  now: Date;
}

export function ProposalExpiry({ expiresAt, now }: ProposalExpiryProps) {
  const { t } = useTranslation("approval");
  const presentation = getExpiryPresentation(expiresAt, now, t);
  return (
    <Badge
      variant="outline"
      className={cn(
        "font-mono tabular-nums",
        presentation.tone === "warning" &&
          "border-warning/20 bg-warning/10 text-warning-foreground",
        presentation.tone === "destructive" &&
          "border-destructive/20 bg-destructive/10 text-destructive-foreground",
        presentation.tone === "neutral" && "text-muted-foreground",
      )}
    >
      {presentation.label}
    </Badge>
  );
}
